import { useEffect, useRef } from "react";
import type { ActionRecord, Message } from "@ai-sales/contracts";

export const WEBSITE_INACTIVITY_REMINDER_MS = 5 * 60 * 1_000;

const serviceIntentPattern = /投诉|故障|告警|报修|售后|异常|没处理|没人回复|设备问题/;
const resourceIntentPattern = /资料|参数表|规格书|手册|下载|产品参数|技术文件/;
const projectIntentPattern = /项目|方案|储能|MW|MWh|投运|削峰填谷|需量|顾问|联系|报价/i;
const reminderStoragePrefix = "ai-sales:website-follow-up:v1:";

function wasReminderSent(conversationId: string): boolean {
  try {
    return window.sessionStorage.getItem(`${reminderStoragePrefix}${conversationId}`) === "sent";
  } catch {
    return false;
  }
}

function rememberReminderSent(conversationId: string): void {
  try {
    window.sessionStorage.setItem(`${reminderStoragePrefix}${conversationId}`, "sent");
  } catch {
    // Storage can be unavailable in privacy-restricted browsers; the in-memory guard still applies.
  }
}

function latestPendingAction(actions: ActionRecord[]): ActionRecord | undefined {
  return actions
    .filter((action) => action.state === "pending_confirmation")
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0];
}

function isComplaint(action: ActionRecord): boolean {
  if (action.plan.objectApiName !== "Case") return false;
  const fieldsText = JSON.stringify(action.plan.fields);
  return serviceIntentPattern.test(`${action.plan.summary} ${fieldsText}`);
}

export function hasCompletedWebsiteSubmission(actions: ActionRecord[]): boolean {
  return actions.some((action) => action.state === "succeeded" && action.plan.operation === "create" &&
    (action.plan.objectApiName === "Lead" || action.plan.objectApiName === "Case"));
}

export function selectWebsiteFollowUpMessage(messages: Message[], actions: ActionRecord[]): string {
  const pending = latestPendingAction(actions);
  if (pending?.plan.objectApiName === "Lead") {
    return "刚才整理的项目咨询尚未提交。您可以继续补充或修改，确认信息无误后再提交；当前内容会暂时为您保留。";
  }
  if (pending && isComplaint(pending)) {
    return "刚才的问题反馈尚未提交。您可以继续补充发生时间、设备现象或现场处理情况，确认后我们会尽快安排人员跟进。";
  }
  if (pending?.plan.objectApiName === "Case") {
    return "刚才整理的服务申请尚未提交。您可以继续补充或修改问题信息，确认无误后再提交。";
  }
  if (pending) {
    return "刚才整理的内容尚未提交。您可以继续补充或修改，确认信息无误后再提交；当前内容会暂时为您保留。";
  }

  const recentUserText = messages
    .filter((message) => message.role === "user")
    .slice(-4)
    .map((message) => message.content)
    .join(" ");
  if (resourceIntentPattern.test(recentUserText)) {
    return "刚才的资料仍可继续查看。如果您需要其他产品参数、安装要求或方案资料，可以直接告诉我。";
  }
  if (serviceIntentPattern.test(recentUserText)) {
    return "如果设备问题仍未解决，您可以继续补充发生时间、设备现象或现场处理情况，我会帮您整理后续服务所需的信息。";
  }
  if (projectIntentPattern.test(recentUserText)) {
    return "如果您正在整理项目信息，可以继续补充项目地点、预计规模或计划投运时间，我可以帮您整理成完整需求，方便后续安排专业人员联系。";
  }
  return "刚才的咨询内容还在。如果您还想了解产品参数、应用方案或项目配置，可以继续告诉我您的需求。";
}

interface WebsiteFollowUpOptions {
  enabled: boolean;
  conversationId?: string;
  messages: Message[];
  actions: ActionRecord[];
  busy: boolean;
  hasDraftInput: boolean;
  activityKey: string;
  onReminder: (content: string) => void;
  delayMs?: number;
}

export function useWebsiteInactivityFollowUp({
  enabled,
  conversationId,
  messages,
  actions,
  busy,
  hasDraftInput,
  activityKey,
  onReminder,
  delayMs = WEBSITE_INACTIVITY_REMINDER_MS
}: WebsiteFollowUpOptions): void {
  const sentConversationRef = useRef<string | undefined>(undefined);
  const activeConversationRef = useRef<string | undefined>(undefined);
  const onReminderRef = useRef(onReminder);

  useEffect(() => {
    onReminderRef.current = onReminder;
  }, [onReminder]);

  useEffect(() => {
    if (activeConversationRef.current === conversationId) return;
    activeConversationRef.current = conversationId;
    sentConversationRef.current = conversationId && wasReminderSent(conversationId) ? conversationId : undefined;
  }, [conversationId]);

  useEffect(() => {
    const latestMessage = messages[messages.length - 1];
    const hasUserInteraction = messages.some((message) => message.role === "user");
    const shouldSchedule = enabled && Boolean(conversationId) && !busy && !hasDraftInput && hasUserInteraction &&
      latestMessage?.role === "assistant" && sentConversationRef.current !== conversationId &&
      !hasCompletedWebsiteSubmission(actions);
    if (!shouldSchedule || !conversationId) return;

    const reminder = selectWebsiteFollowUpMessage(messages, actions);
    const timer = window.setTimeout(() => {
      if (sentConversationRef.current === conversationId) return;
      sentConversationRef.current = conversationId;
      rememberReminderSent(conversationId);
      onReminderRef.current(reminder);
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [actions, activityKey, busy, conversationId, delayMs, enabled, hasDraftInput, messages]);
}
